import apolloClient from '../apolloClient'
import configQueries from '../graphql/config'
import { cachedGeocode, cachedSuggest, suggestFromCacheBySubstring } from './yandexAddressCache'

// Ключ HTTP Геокодера приходит с бэкенда (тот же конфиг, что в ymapsSettings.js).
// Запрашивается лениво при первом геокодировании и переиспользуется до перезагрузки страницы.
let geocoderApiKeyPromise = null

const getGeocoderApiKey = () => {
  if (geocoderApiKeyPromise === null) {
    geocoderApiKeyPromise = apolloClient.query({
      query: configQueries.configsBatchGet,
      variables: {
        paramNames: ['ymaps_api_geocode_key']
      }
    }).then(res => {
      const nodes = (res.data && res.data.configsBatchGet && res.data.configsBatchGet.nodes) || []
      const conf = nodes.find(c => c.param === 'ymaps_api_geocode_key')
      if (!conf || !conf.value) { throw new Error('Не удалось получить ymaps_api_geocode_key') }
      return conf.value
    }).catch(error => {
      // сбрасываем, чтобы следующий вызов повторил запрос ключа
      geocoderApiKeyPromise = null
      throw error
    })
  }
  return geocoderApiKeyPromise
}

// "Живой" GeoObjectCollection нельзя сохранить в localStorage (в нём методы и внутренние
// ссылки). Поэтому кэшируем geocode в JSON-виде и на каждое обращение - хоть из кэша,
// хоть свежее - пересобираем из этого JSON настоящий GeoObjectCollection с нуля.
const parsePos = (pos) => {
  const [lon, lat] = pos.split(' ').map(Number)
  return [lat, lon]
}

const buildGeoObjectCollection = (jsonResponse) => {
  const geoObjectCollection = jsonResponse.GeoObjectCollection || {}
  const featureMember = geoObjectCollection.featureMember || []
  const responseMetaData = (geoObjectCollection.metaDataProperty && geoObjectCollection.metaDataProperty.GeocoderResponseMetaData) || {}

  const collection = new window.ymaps.GeoObjectCollection()

  // Объединённые границы всех результатов - копятся по мере разбора
  let unionBounds = null
  const extendUnionBounds = (p1, p2) => {
    if (unionBounds === null) {
      unionBounds = [[Math.min(p1[0], p2[0]), Math.min(p1[1], p2[1])], [Math.max(p1[0], p2[0]), Math.max(p1[1], p2[1])]]
      return
    }
    unionBounds[0][0] = Math.min(unionBounds[0][0], p1[0], p2[0])
    unionBounds[0][1] = Math.min(unionBounds[0][1], p1[1], p2[1])
    unionBounds[1][0] = Math.max(unionBounds[1][0], p1[0], p2[0])
    unionBounds[1][1] = Math.max(unionBounds[1][1], p1[1], p2[1])
  }

  featureMember.forEach(({GeoObject}) => {
    if (!GeoObject || !GeoObject.Point) return

    const coords = parsePos(GeoObject.Point.pos)
    const properties = {
      name: GeoObject.name,
      description: GeoObject.description,
      balloonContentBody: GeoObject.description,
      metaDataProperty: GeoObject.metaDataProperty
    }

    if (GeoObject.boundedBy && GeoObject.boundedBy.Envelope) {
      const {lowerCorner, upperCorner} = GeoObject.boundedBy.Envelope
      properties.boundedBy = [parsePos(lowerCorner), parsePos(upperCorner)]
      extendUnionBounds(properties.boundedBy[0], properties.boundedBy[1])
    } else {
      extendUnionBounds(coords, coords)
    }

    collection.add(new window.ymaps.GeoObject({
      geometry: {
        type: 'Point',
        coordinates: coords
      },
      properties
    }))
  })

  const geocoderMetaData = {
    request: responseMetaData.request,
    found: Number(responseMetaData.found) || collection.getLength(),
    results: Number(responseMetaData.results) || collection.getLength(),
    skip: Number(responseMetaData.skip) || 0
  }
  // SearchControl автоматически центрирует карту после поиска только если в метаданных
  // есть boundedBy (границы всего набора результатов) либо found == 1 - иначе результаты
  // появляются в списке, но карта остаётся на месте.
  if (unionBounds !== null) { geocoderMetaData.boundedBy = unionBounds }

  return {
    geoObjects: collection,
    metaData: {
      geocoder: geocoderMetaData
    }
  }
}

// SearchControl передаёт в options.provider ссылку на сам наш provider (это его штатное
// поведение - весь конфиг контрола прокидывается в options при каждом вызове). Если пробросить
// options как есть в window.ymaps.suggest, Яндекс увидит в options.provider объект с методом
// suggest и сам его вызовет - получится бесконечный рикошет через наш же provider.
// Поэтому перед обращением к нативному API оставляем только безопасные поля.
const pickNativeOptions = (options) => ({
  boundedBy: options && options.boundedBy,
  results: options && options.results,
  strictBounds: options && options.strictBounds
})

// Кастомный провайдер для ymaps.control.SearchControl (options.provider):
// - suggest() отрабатывает на каждый ввод в поле поиска на карте (дешёвые подсказки);
// - geocode() отрабатывает только когда пользователь подтверждает поиск (Enter/кнопка/выбор
//   подсказки) - так решает сам SearchControl, здесь мы это не выбираем.
// Оба метода используют persistent-кэш (localStorage, 7 дней) из yandexAddressCache.js.
const SUGGEST_DEBOUNCE_MS = 700
// Подсказки короче 5 символов бесполезны для адреса, а дневной лимит саджеста едят
const SUGGEST_MIN_LENGTH = 5
let suggestDebounceTimer = null
let pendingSuggestDeferreds = []
let lastGeocodedRequest = null

export const yandexSearchProvider = {
  // Debounce: пока пользователь печатает, запрос не уходит; через SUGGEST_DEBOUNCE_MS тишины
  // уходит ОДИН запрос с последним введённым текстом. Просто "проглотить" промежуточные вызовы
  // нельзя - SearchControl ждёт промис от каждого, поэтому копим их deferred-ы и резолвим
  // все результатом последнего запроса (панель подсказок всё равно показывает только его).
  suggest (request, options) {
    const normalizedRequest = (request || '').trim()

    if (normalizedRequest.length < SUGGEST_MIN_LENGTH) {
      return window.ymaps.vow.resolve([])
    }

    // После подтверждения поиска SearchControl сам дергает suggest с финальным адресом,
    // хотя панель подсказок уже закрыта - глушим этот один холостой запрос.
    if (lastGeocodedRequest !== null && normalizedRequest.toLowerCase() === lastGeocodedRequest) {
      lastGeocodedRequest = null
      return window.ymaps.vow.resolve([])
    }

    const deferred = window.ymaps.vow.defer()
    pendingSuggestDeferreds.push(deferred)
    const nativeOptions = pickNativeOptions(options)

    clearTimeout(suggestDebounceTimer)
    suggestDebounceTimer = setTimeout(() => {
      const deferreds = pendingSuggestDeferreds
      pendingSuggestDeferreds = []
      cachedSuggest(request, nativeOptions)
        .then(result => deferreds.forEach(d => d.resolve(result)))
        .catch(() => {
          // Сетевой саджест недоступен (исчерпан дневной лимит / нет сети) - показываем
          // хоть что-то из закэшированных подсказок по подстроке вместо пустой панели.
          const fallback = suggestFromCacheBySubstring(request, nativeOptions.results)
          deferreds.forEach(d => d.resolve(fallback))
        })
    }, SUGGEST_DEBOUNCE_MS)

    return deferred.promise()
  },
  geocode (request, options) {
    lastGeocodedRequest = (request || '').trim().toLowerCase()
    // SearchControl ждёт от provider ymaps.vow.Promise (с .done()/.fail()), а цепочка
    // из getGeocoderApiKey/cachedGeocode возвращает нативный Promise - конвертируем
    // через vow.defer().
    const deferred = window.ymaps.vow.defer()
    getGeocoderApiKey()
      .then(apikey => cachedGeocode(request, {...pickNativeOptions(options), apikey}))
      .then(jsonResponse => deferred.resolve(buildGeoObjectCollection(jsonResponse)))
      .catch(error => {
        deferred.reject(error)
      })
    return deferred.promise()
  }
}



// WEBPACK FOOTER //
// ./src/helpers/yandexSearchControlProvider.js